import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Collapse,
  IconButton,
  TableSortLabel,
  Divider,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Logo import
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

// Category colors
const CATEGORY_COLORS = {
  // New split categories
  'CANDY': '#e91e63',        // Pink - stands out for candy
  'SNACKS': '#ff9800',       // Orange - for non-candy convenience food
  'BEVERAGES': '#2196f3',    // Blue - for beverages
  // Original categories (some may still appear)
  'CONVENIENCE FOOD': '#ff9800',  // Legacy - now split into CANDY/SNACKS
  'NON ALCOHOLIC BEV': '#2196f3', // Legacy - now BEVERAGES
  'HBA': '#4caf50',
  'ELECTRONICS': '#9c27b0',
  'PREMIUM ELECTRONICS': '#7b1fa2',
  'DRINKWARE': '#00bcd4',
  'TRAVEL': '#795548',       // Renamed from PILLOWS/BLANKETS
  'PILLOWS/BLANKETS': '#795548',  // Legacy
  'TOYS': '#f06292',
  'GIFTS': '#f44336',
  'APPAREL': '#3f51b5',
  'SPORTS': '#558b2f',
  'EYEWEAR': '#00838f',
  'UNKNOWN': '#9e9e9e'
};

// Platform colors
const PLATFORM_COLORS = {
  'ATL': '#1976d2',
  'MEM': '#388e3c',
  'BNA': '#d32f2f',
  'CAE': '#7b1fa2',
  'GSO': '#f57c00',
  'PNS': '#0097a7',
  'RDU': '#c2185b',
  'SAV': '#689f38',
  'TYS': '#455a64',
  'ASH': '#5d4037',
  'UNKNOWN': '#9e9e9e'
};

const OBDDashboard = () => {
  // State
  const [productivity, setProductivity] = useState(null);
  const [workRemaining, setWorkRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Sorting for associate table
  const [sortColumn, setSortColumn] = useState('obds_completed');
  const [sortDirection, setSortDirection] = useState('desc');

  // Expanded departures
  const [expandedPlatforms, setExpandedPlatforms] = useState({});

  // Live Activity Roster (read-only mirror of the Activity Monitor roster).
  // Fetched independently of the OBD stats — a roster failure only degrades
  // this card, never the rest of the dashboard.
  const [roster, setRoster] = useState([]);
  const [rosterError, setRosterError] = useState(false);
  const [rosterTime, setRosterTime] = useState(new Date()); // 1s tick for elapsed timers

  // Reuses the Activity Monitor's endpoint (GET /api/clock/active) — no new
  // endpoint. JWT from localStorage, same convention as the rest of the app.
  const fetchRoster = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/clock/active`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`roster ${res.status}`);
      const data = await res.json();
      setRoster(Array.isArray(data) ? data : []);
      setRosterError(false);
    } catch (err) {
      console.error('Live roster fetch failed (card degrades quietly):', err.message);
      setRosterError(true);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
    const interval = setInterval(fetchRoster, 30000); // same cadence as Activity Monitor
    return () => clearInterval(interval);
  }, [fetchRoster]);

  useEffect(() => {
    const t = setInterval(() => setRosterTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch productivity data
  const fetchProductivity = useCallback(async () => {
    try {
      console.log('📊 Fetching productivity from:', `${API_BASE}/api/obd/dashboard/productivity`);
      const prodToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/obd/dashboard/productivity`, {
        headers: prodToken ? { Authorization: `Bearer ${prodToken}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Productivity data received:', data);
        console.log('📊 Associates:', data?.associates?.length || 0);
        console.log('📊 Building total:', data?.buildingTotal);
        setProductivity(data);
      } else {
        console.error('📊 Productivity fetch failed:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Error fetching productivity:', err);
    }
  }, []);

  // Fetch work remaining data
  const fetchWorkRemaining = useCallback(async () => {
    try {
      console.log('📋 Fetching work remaining from:', `${API_BASE}/api/obd/dashboard/work-remaining`);
      const response = await fetch(`${API_BASE}/api/obd/dashboard/work-remaining`);
      if (response.ok) {
        const data = await response.json();
        console.log('📋 Work remaining data received:', data);
        console.log('📋 Departures:', data?.departures?.length || 0);
        console.log('📋 Overall estimate:', data?.overallEstimate);
        setWorkRemaining(data);
      } else {
        console.error('📋 Work remaining fetch failed:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Error fetching work remaining:', err);
    }
  }, []);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchProductivity(), fetchWorkRemaining()]);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [fetchProductivity, fetchWorkRemaining]);

  // Initial load and auto-refresh
  useEffect(() => {
    fetchAllData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Sort associates
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedAssociates = productivity?.associates?.slice().sort((a, b) => {
    const aVal = a[sortColumn] || 0;
    const bVal = b[sortColumn] || 0;
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  }) || [];

  // Toggle platform expansion
  const togglePlatform = (platform) => {
    setExpandedPlatforms(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  // Format time
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Get platform chip
  const getPlatformChip = (platform) => {
    const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS['UNKNOWN'];
    return (
      <Chip
        label={platform || '?'}
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 600,
          minWidth: 50
        }}
      />
    );
  };

  // Get category chip
  const getCategoryChip = (category) => {
    const color = CATEGORY_COLORS[category] || CATEGORY_COLORS['UNKNOWN'];
    return (
      <Chip
        label={category || 'UNKNOWN'}
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 500
        }}
      />
    );
  };

  // Render Building Performance
  const renderBuildingPerformance = () => {
    const bt = productivity?.buildingTotal || {};

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon /> Building Performance (Today)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={2.4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {bt.obds_completed || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">OBDs Completed</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#f3e5f5' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                {bt.total_lines || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">Lines Completed</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e9' }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {bt.total_units || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">Units Completed</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fff3e0' }}>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {bt.lines_per_hour || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">Lines/Hour</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fce4ec' }}>
              <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
                {bt.units_per_hour || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">Units/Hour</Typography>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Render Associate Performance Table
  const renderAssociatePerformance = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon /> Associate Performance (Today)
      </Typography>

      {sortedAssociates.length === 0 ? (
        <Alert severity="info">No completed OBDs today yet. Performance data will appear as associates complete work.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Associate</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'obds_completed'}
                    direction={sortColumn === 'obds_completed' ? sortDirection : 'desc'}
                    onClick={() => handleSort('obds_completed')}
                  >
                    OBDs
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'total_lines'}
                    direction={sortColumn === 'total_lines' ? sortDirection : 'desc'}
                    onClick={() => handleSort('total_lines')}
                  >
                    Lines
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'total_units'}
                    direction={sortColumn === 'total_units' ? sortDirection : 'desc'}
                    onClick={() => handleSort('total_units')}
                  >
                    Units
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'lines_per_hour'}
                    direction={sortColumn === 'lines_per_hour' ? sortDirection : 'desc'}
                    onClick={() => handleSort('lines_per_hour')}
                  >
                    LPH
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'units_per_hour'}
                    direction={sortColumn === 'units_per_hour' ? sortDirection : 'desc'}
                    onClick={() => handleSort('units_per_hour')}
                  >
                    UPH
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortColumn === 'avg_minutes_per_obd'}
                    direction={sortColumn === 'avg_minutes_per_obd' ? sortDirection : 'desc'}
                    onClick={() => handleSort('avg_minutes_per_obd')}
                  >
                    Avg Min/OBD
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current OBD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAssociates.map((assoc, idx) => (
                <TableRow
                  key={assoc.name}
                  sx={{
                    backgroundColor: assoc.current_obd ? '#fff3e0' : 'inherit',
                    '&:hover': { backgroundColor: '#f0f0f0' }
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>
                    {assoc.name}
                    {assoc.current_obd && (
                      <Chip
                        label="Active"
                        size="small"
                        color="warning"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{assoc.obds_completed}</TableCell>
                  <TableCell>{assoc.total_lines}</TableCell>
                  <TableCell>{assoc.total_units}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#ff9800' }}>{assoc.lines_per_hour}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#e91e63' }}>{assoc.units_per_hour}</TableCell>
                  <TableCell>{assoc.avg_minutes_per_obd}m</TableCell>
                  <TableCell>
                    {assoc.current_obd ? (
                      <Tooltip title={`${assoc.current_obd.total_lines} lines, ${assoc.current_obd.total_items} units`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {assoc.current_obd.obd_number}
                          </Typography>
                          {getCategoryChip(assoc.current_obd.category)}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  // Render Work Remaining by Departure
  const renderWorkRemaining = () => {
    const departures = workRemaining?.departures || [];
    const overall = workRemaining?.overallEstimate || {};

    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon /> Work Remaining by Departure
        </Typography>

        {/* Overall Summary */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Total OBDs</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{overall.totalRemainingOBDs || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Total Lines</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#7b1fa2' }}>{overall.totalRemainingLines || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Total Units</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#4caf50' }}>{overall.totalRemainingUnits || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">Est. Hours Remaining</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#f44336' }}>{overall.estimatedHoursRemaining || 0}h</Typography>
            </Grid>
          </Grid>

          {/* Overall Progress Bar */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Overall Progress
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {overall.completedLines || 0} / {overall.totalLines || 0} lines ({overall.completionPercent || 0}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={overall.completionPercent || 0}
              sx={{
                height: 16,
                borderRadius: 8,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: (overall.completionPercent || 0) > 75
                    ? '#4caf50'
                    : (overall.completionPercent || 0) >= 25
                    ? '#ff9800'
                    : '#f44336',
                  borderRadius: 8
                }
              }}
            />
          </Box>
        </Box>

        {departures.length === 0 ? (
          <Alert severity="info">No pending OBDs. All work has been completed!</Alert>
        ) : (
          <Box>
            {departures.map((dep) => {
              const isExpanded = expandedPlatforms[dep.platform];
              const platformColor = PLATFORM_COLORS[dep.platform] || '#757575';
              const completionPercent = dep.completionPercent || 0;
              // Subtle fill color based on progress
              const fillColor = completionPercent > 75 ? 'rgba(76, 175, 80, 0.15)' : completionPercent >= 25 ? 'rgba(255, 152, 0, 0.15)' : 'rgba(244, 67, 54, 0.1)';

              return (
                <Paper
                  key={dep.platform}
                  sx={{
                    mb: 2,
                    border: `2px solid ${platformColor}`,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {/* Header with fill effect */}
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      // Fill effect: gradient from left to right based on completion
                      background: `linear-gradient(to right, ${fillColor} ${completionPercent}%, #fafafa ${completionPercent}%)`,
                      '&:hover': {
                        background: `linear-gradient(to right, ${fillColor} ${completionPercent}%, #f0f0f0 ${completionPercent}%)`
                      }
                    }}
                    onClick={() => togglePlatform(dep.platform)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getPlatformChip(dep.platform)}
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {dep.departureTime ? `Departs ${formatTime(dep.departureTime)}` : 'No delivery today'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {dep.obdCount} OBDs | {dep.pendingLines || dep.totalLines} lines | {dep.pendingUnits || dep.totalUnits} units | Est. {dep.estimatedHours}h work
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Progress indicator */}
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: completionPercent > 75 ? '#4caf50' : completionPercent >= 25 ? '#ff9800' : '#f44336' }}>
                          {completionPercent}%
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {dep.completedLines || 0}/{dep.totalLines || 0} lines
                        </Typography>
                      </Box>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Expanded OBD List */}
                  <Collapse in={isExpanded}>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>OBD</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Lines</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Units</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Est. Min</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Worker</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dep.obds.map((obd) => (
                              <TableRow
                                key={obd.obd_number}
                                sx={{
                                  backgroundColor: obd.status === 'In Progress' ? '#fff3e0' : 'inherit'
                                }}
                              >
                                <TableCell sx={{ fontWeight: 600 }}>{obd.obd_number}</TableCell>
                                <TableCell>{getCategoryChip(obd.category)}</TableCell>
                                <TableCell>{obd.lines}</TableCell>
                                <TableCell>{obd.units}</TableCell>
                                <TableCell>{obd.estimated_minutes}m</TableCell>
                                <TableCell>
                                  <Chip
                                    label={obd.status}
                                    size="small"
                                    color={obd.status === 'In Progress' ? 'warning' : 'success'}
                                  />
                                </TableCell>
                                <TableCell>{obd.assigned_worker || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>
    );
  };

  // Live Activity Roster card — READ-ONLY. Management controls (force in/out,
  // bulk check-out, toggles) live exclusively on the Activity Monitor page.
  // Chips match the Activity Monitor roster's styling.
  const renderLiveRoster = () => {
    const rosterStatusChip = (status, breakType) => {
      if (status === 'clocked_in') return <Chip label="Checked In" size="small" sx={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 600 }} />;
      if (status === 'on_break') {
        if (breakType === 'lunch') return <Chip label="On Lunch" size="small" sx={{ backgroundColor: '#7b1fa2', color: '#fff', fontWeight: 600 }} />;
        return <Chip label="On Break" size="small" sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 600 }} />;
      }
      return <Chip label="Checked Out" size="small" variant="outlined" />;
    };
    const fmtElapsed = (iso) => {
      const s = Math.max(0, Math.floor((rosterTime - new Date(iso)) / 1000));
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
      return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    };

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Live Activity Roster
          </Typography>
          {!rosterError && (
            <Chip
              label={`${roster.length} checked in`}
              size="small"
              sx={{ ml: 1, backgroundColor: roster.length > 0 ? '#4caf50' : '#9e9e9e', color: '#fff' }}
            />
          )}
        </Box>

        {rosterError ? (
          <Typography variant="body2" color="textSecondary">
            Roster temporarily unavailable.
          </Typography>
        ) : roster.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No one is currently checked in.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Elapsed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roster.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ backgroundColor: row.status === 'on_break' ? (row.current_break_type === 'lunch' ? '#f3e5f5' : '#fff8e1') : 'inherit' }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.worker_name}</TableCell>
                    <TableCell>{rosterStatusChip(row.status, row.current_break_type)}</TableCell>
                    <TableCell>
                      {row.current_category_label
                        ? <Chip
                            label={row.current_vendor_name ? `${row.current_category_label} — ${row.current_vendor_name}` : row.current_category_label}
                            size="small" variant="outlined" color="primary" sx={{ fontWeight: 600 }} />
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{fmtElapsed(row.clock_in_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            DC Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last updated: {lastRefresh.toLocaleTimeString()} | Auto-refreshes every 30 seconds
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchAllData}
          disabled={loading}
        >
          Refresh Now
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Live Activity Roster (read-only mirror of the Activity Monitor) */}
      {renderLiveRoster()}

      {/* Building Performance */}
      {renderBuildingPerformance()}

      {/* Associate Performance */}
      {renderAssociatePerformance()}

      {/* Work Remaining by Departure */}
      {renderWorkRemaining()}

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>
    </Container>
  );
};

export default OBDDashboard;
